import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { App } from '@capacitor/app'
import { supabase } from '../../shared/lib/supabase'
import { SignInWithApple } from '@capacitor-community/apple-sign-in'

const AUTH_CALLBACK_URL = 'bot.tend.app://auth-callback'

interface AuthContextValue {
  user: User | null
  session: Session | null
  loading: boolean
  needsPasswordSet: boolean
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: string | null }>
  updatePassword: (password: string) => Promise<{ error: string | null }>
  signInWithGoogle: () => Promise<{ error: string | null }>
  signInWithApple: () => Promise<{ error: string | null }>
  clearPasswordSet: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  // Detect invite or recovery tokens in the URL hash before Supabase processes them
  const [needsPasswordSet, setNeedsPasswordSet] = useState(() => {
    const hash = window.location.hash
    return hash.includes('type=invite') || hash.includes('type=recovery')
  })

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        // Identify user in Sentry for error correlation
        if (session?.user) {
          Sentry.setUser({ id: session.user.id, email: session.user.email })
        } else {
          Sentry.setUser(null)
        }
        if (event === 'PASSWORD_RECOVERY') {
          setNeedsPasswordSet(true)
        }
      },
    )

    return () => subscription.unsubscribe()
  }, [])

  // Listen for deep link callbacks from native OAuth (SFSafariViewController / Chrome Custom Tab)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const listener = App.addListener('appUrlOpen', async ({ url }) => {
      if (!url.includes('auth-callback')) return

      // Supabase appends tokens as hash fragment: #access_token=...&refresh_token=...
      const hashPart = url.split('#')[1]
      if (!hashPart) return

      const params = new URLSearchParams(hashPart)
      const accessToken = params.get('access_token')
      const refreshToken = params.get('refresh_token')

      if (accessToken && refreshToken) {
        await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
      }

      await Browser.close()
    })

    return () => { listener.then(h => h.remove()) }
  }, [])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error: error?.message ?? null }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email)
    return { error: error?.message ?? null }
  }

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password })
    return { error: error?.message ?? null }
  }

  const clearPasswordSet = () => setNeedsPasswordSet(false)

  const signInWithGoogle = async () => {
    if (Capacitor.isNativePlatform()) {
      // On native iOS/Android, open OAuth in system browser (SFSafariViewController / Chrome Custom Tab)
      // instead of the WKWebView, which Google blocks.
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: AUTH_CALLBACK_URL,
          skipBrowserRedirect: true,
        },
      })
      if (error) return { error: error.message }
      if (data.url) {
        await Browser.open({ url: data.url, presentationStyle: 'popover' })
      }
      return { error: null }
    }

    // Web: standard redirect flow
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    })
    return { error: error?.message ?? null }
  }

  const signInWithApple = async () => {
    if (Capacitor.isNativePlatform()) {
      // Native iOS: use system Sign in with Apple sheet, then exchange token with Supabase
      try {
        const result = await SignInWithApple.authorize({
          clientId: 'bot.tend.app',
          scopes: 'email name',
          redirectURI: AUTH_CALLBACK_URL,
        })
        const { data, error } = await supabase.auth.signInWithIdToken({
          provider: 'apple',
          token: result.response.identityToken,
        })
        if (error) {
          Sentry.captureMessage(`Apple Sign In: signInWithIdToken failed — ${error.message}`, 'error')
          return { error: error.message }
        }
        if (!data.session) {
          Sentry.captureMessage('Apple Sign In: no session returned from signInWithIdToken', 'error')
          return { error: 'Sign-in succeeded but no session was created' }
        }
        // Apple provides name only on first authorization — persist it in user metadata
        // so onboarding can pre-fill the name step (Apple guideline 4 compliance)
        const givenName = result.response.givenName
        const familyName = result.response.familyName
        if (givenName || familyName) {
          await supabase.auth.updateUser({
            data: {
              ...(givenName ? { given_name: givenName } : {}),
              ...(familyName ? { family_name: familyName } : {}),
              full_name: [givenName, familyName].filter(Boolean).join(' '),
            },
          })
        }
        // signInWithIdToken already triggers onAuthStateChange — no extra setSession needed
        return { error: null }
      } catch (e) {
        // User cancelled the Apple sign-in sheet. iOS reports 1001 for explicit cancel,
        // but 1000 ("unknown") is the most common code for tap-outside / swipe-down / restricted
        // device — not a real error we can act on.
        const msg = e instanceof Error ? e.message : 'Apple sign-in failed'
        if (msg.includes('1001') || msg.includes('1000')) return { error: null }
        Sentry.captureException(e, { tags: { flow: 'apple-sign-in' } })
        return { error: msg }
      }
    }

    // Web: OAuth redirect flow
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: window.location.origin },
    })
    return { error: error?.message ?? null }
  }

  return (
    <AuthContext.Provider value={{ user, session, loading, needsPasswordSet, signIn, signOut, resetPassword, updatePassword, signInWithGoogle, signInWithApple, clearPasswordSet }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
